// §K — the crew page's write-back layer: thin wrappers over the anon
// crew_portal_* SECDEF RPCs (the slug is the credential; settings are
// re-enforced server-side) + the crew-notify owner-push relay (fire-and-forget
// after each successful action). Every action returns the envelope so the UI
// shows explicit success/error (anti-silent-operations).

import { useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import type { CrewJobState, CrewMessage } from '../../types/crewPortal';

type Envelope = { success: boolean; deduped?: boolean; error?: { code: string } } & Record<string, unknown>;

export type NotifyKind = 'opened' | 'accepted' | 'arrived' | 'completed' | 'pay_requested' | 'reply';

export function notifyOwner(slug: string, kind: NotifyKind, workOrderId?: string): void {
  // fire-and-forget — a push failure never blocks the crew's action
  void supabase.functions
    .invoke('crew-notify', { body: { slug, kind, work_order_id: workOrderId ?? null } })
    .catch(() => {});
}

export function useCrewLifecycle(slug: string, workOrderId: string, initial: {
  state: CrewJobState | null;
  messages: CrewMessage[];
}) {
  const [state, setState] = useState<CrewJobState | null>(initial.state);
  const [messages, setMessages] = useState<CrewMessage[]>(initial.messages);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const patch = useCallback((p: Partial<CrewJobState>) => {
    setState((prev) => ({
      accepted_at: null, arrived_at: null, pay_cents: null, pay_requested_at: null,
      paid_at: null, paid_method: null, receipt_ref: null, checklist_state: null,
      ...(prev ?? {}),
      ...p,
    }));
  }, []);

  const run = useCallback(async (
    action: string,
    rpc: string,
    args: Record<string, unknown>,
    onSuccess: (env: Envelope) => void,
    notifyKind?: NotifyKind,
  ): Promise<Envelope> => {
    setBusy(action);
    setError(null);
    const { data, error: rpcErr } = await supabase.rpc(rpc, args);
    setBusy(null);
    const env = (data ?? { success: false, error: { code: 'NETWORK' } }) as Envelope;
    if (rpcErr) {
      setError('Something went wrong — check your connection and try again.');
      return { success: false, error: { code: 'NETWORK' } };
    }
    if (env.success) {
      onSuccess(env);
      if (notifyKind && !env.deduped) notifyOwner(slug, notifyKind, workOrderId);
    } else {
      setError(ERROR_COPY[env.error?.code ?? ''] ?? 'That didn’t go through — try again.');
    }
    return env;
  }, [slug, workOrderId]);

  const accept = useCallback(() =>
    run('accept', 'crew_portal_accept', { p_slug: slug, p_work_order_id: workOrderId },
      (env) => patch({ accepted_at: (env.accepted_at as string) ?? new Date().toISOString() }), 'accepted'),
    [run, slug, workOrderId, patch]);

  const arrive = useCallback((lat: number, lng: number) =>
    run('arrive', 'crew_portal_arrive', { p_slug: slug, p_work_order_id: workOrderId, p_lat: lat, p_lng: lng },
      (env) => patch({ arrived_at: (env.arrived_at as string) ?? new Date().toISOString() }), 'arrived'),
    [run, slug, workOrderId, patch]);

  const setChecklist = useCallback((stateObj: Record<string, boolean>) =>
    run('checklist', 'crew_portal_set_checklist', { p_slug: slug, p_work_order_id: workOrderId, p_state: stateObj },
      () => patch({ checklist_state: stateObj })),
    [run, slug, workOrderId, patch]);

  const complete = useCallback(() =>
    run('complete', 'crew_portal_complete', { p_slug: slug, p_work_order_id: workOrderId },
      () => { /* the caller flips its job status */ }, 'completed'),
    [run, slug, workOrderId]);

  const requestPay = useCallback(() =>
    run('requestPay', 'crew_portal_request_pay', { p_slug: slug, p_work_order_id: workOrderId },
      (env) => patch({ pay_requested_at: (env.pay_requested_at as string) ?? new Date().toISOString() }), 'pay_requested'),
    [run, slug, workOrderId, patch]);

  const reply = useCallback((body: string) =>
    run('reply', 'crew_portal_reply', { p_slug: slug, p_work_order_id: workOrderId, p_body: body },
      (env) => setMessages((prev) => [...prev, {
        id: (env.message_id as string) ?? `local-${prev.length}`,
        author: 'crew', from_voice: null, body, created_at: new Date().toISOString(),
      }]), 'reply'),
    [run, slug, workOrderId]);

  return { state, messages, busy, error, accept, arrive, setChecklist, complete, requestPay, reply };
}

const ERROR_COPY: Record<string, string> = {
  PORTAL_NOT_FOUND: 'This schedule page is no longer active.',
  JOB_NOT_YOURS: 'This job isn’t on your schedule.',
  ACCEPT_NOT_REQUIRED: 'No acceptance needed for this job.',
  GPS_DISABLED: 'Check-in isn’t turned on for your team.',
  CHECKLIST_INCOMPLETE: 'Finish the checklist to mark this done.',
  NOT_COMPLETED: 'Mark the job complete first.',
  PAY_LANE_CONTRACTOR_ONLY: 'Per-job pay isn’t available on this portal — your pay runs through payroll.',
  JOB_CANCELLED: 'This job was cancelled.',
  RATE_LIMITED: 'Slow down a moment, then try again.',
  BODY_INVALID: 'Write a message first.',
  INVALID_STATE: 'That didn’t save — reload and try again.',
};
